'use client'

import { create } from 'zustand'
import { getMemberships } from '@/lib/api'
import type { MembershipsInfo } from '@/lib/api'

interface MembershipsState {
  memberships: MembershipsInfo | null
  isLoading: boolean
  error: Error | null
  inFlight: Promise<void> | null
  fetch: () => Promise<void>
  forceFetch: () => Promise<void>
  clear: () => void
  setReady: () => void
}

/**
 * Shared store for cheap org/team membership data. This backs surfaces that need
 * role or team identity but not billing balances or subscription reconciliation.
 */
export const useMembershipsStore = create<MembershipsState>((set, get) => {
  let counter = 0

  const startFetch = ({ showLoading = false }: { showLoading?: boolean } = {}): Promise<void> => {
    const id = ++counter
    const promise = (async () => {
      try {
        const info = await getMemberships()
        if (id !== counter) return
        set({ memberships: info, error: null, isLoading: false })
      } catch (err) {
        if (id !== counter) return
        set({
          error: err instanceof Error ? err : new Error('Failed to fetch memberships'),
          isLoading: false,
        })
      } finally {
        if (id === counter) set({ inFlight: null })
      }
    })()
    set({ inFlight: promise, ...(showLoading ? { isLoading: true } : {}) })
    return promise
  }

  return {
    memberships: null,
    isLoading: true,
    error: null,
    inFlight: null,

    fetch: () => get().inFlight ?? startFetch(),

    forceFetch: () => startFetch({ showLoading: true }),

    clear: () => {
      counter += 1
      set({ memberships: null, error: null, isLoading: false, inFlight: null })
    },

    setReady: () => set({ isLoading: false }),
  }
})
